import { orderSourceLabel } from '@/utils/orderSource';
/**
 * DesignerBrief - Redesigned order detail panel for florists
 * A "production ticket" layout with recipe card, logistics, recipient, and enclosure message.
 */
import React, { useMemo, useState, useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import { Printer, AlertCircle, Check, Gift, FileText, ExternalLink, ChevronLeft, ChevronRight, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDesignerBriefReferences } from '@/hooks/useDesignerBriefReferences';
import { getPublicShopBranding } from '@/api/shop';
import usePreparedPrint, { captureTrifoldOrder } from '@/hooks/usePreparedPrint';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import useOrderSheetPrint from '@/hooks/useOrderSheetPrint';
import ArrangementPhotoCard from '@/components/ArrangementPhotoCard';
import DesignerNotesCard from '@/components/DesignerNotesCard';
import AuthenticatedDocumentLink from '@/components/AuthenticatedDocumentLink';
import OrderSourcePreview from '@/components/design/OrderSourcePreview';
import { MatchedPhotoTile, ArrangementPhotoTile } from '@/components/design/PhotoTiles';
import TrustBadge from '@/components/design/TrustBadge';
import NoteChip from '@/components/design/NoteChip';
import { useProductReferenceMedia } from '@/hooks/useProductReferenceMedia';
import { resolveTrustState } from '@/utils/designTrust';
import { productReferenceMediaLabel } from '@/utils/productReference';

// Color mapping for known network sources
const SOURCE_STYLES = {
  FTD:          { bg: 'bg-purple-600',  text: 'text-white' },
  Teleflora:    { bg: 'bg-teal-600',    text: 'text-white' },
  LVB:          { bg: 'bg-emerald-700', text: 'text-white' },
  Bloomnet:     { bg: 'bg-blue-600',    text: 'text-white' },
  JustFlowers:  { bg: 'bg-pink-600',    text: 'text-white' },
  Stripe:       { bg: 'bg-indigo-600',  text: 'text-white' },
  'Phone Order':{ bg: 'bg-stone-500',   text: 'text-white' },
};
const DEFAULT_SOURCE_STYLE = { bg: 'bg-stone-600', text: 'text-white' };

const SIZE_STYLES = {
  regular:   'bg-stone-200 text-stone-700',
  standard:  'bg-stone-200 text-stone-700',
  deluxe:    'bg-purple-500 text-white',
  premium:   'bg-amber-500 text-white',
  exquisite: 'bg-rose-600 text-white',
};

// The recipe heading is bounded so a giant wire-service product_name (which the
// parser otherwise promotes wholesale — see the Sharon Young dupe) can't become a
// wall of bold text. A fixed CHARACTER cap — a rough proxy for one desktop row,
// better than a word count since word widths vary — applied identically on
// desktop + mobile, so both show the SAME words; on the narrower mobile they just
// wrap. The full text still lives in the description body below, so nothing is lost.
const HEADING_CAP = 68;
function capHeading(text) {
  if (!text) return text;
  const t = String(text).trim();
  return t.length > HEADING_CAP ? t.slice(0, HEADING_CAP).trimEnd() + '…' : t;
}

/**
 * Parse a product_description string into structured parts.
 * Works across FTD, Teleflora, Bloomnet, Stripe, manual, and other networks.
 */
function parseProductDescription(desc) {
  // Size is no longer parsed out of the description — it's a stored order
  // field (order.size), detected deterministically at ingestion. We leave any
  // "SIZE: X" text in the description body untouched so it stays readable.
  const empty = { code: null, heading: null, body: null, addOns: [], secondChoice: null };
  if (!desc) return empty;

  let remaining = desc;

  // 1. Extract add-ons (e.g., "Add-on: Small Teddy Bear.")
  const addOns = [];
  const addOnRegex = /Add-on:\s*([^.]+?)(?:\.|$)/gi;
  let m;
  while ((m = addOnRegex.exec(remaining)) !== null) {
    addOns.push(m[1].trim());
  }
  remaining = remaining.replace(/Add-on:\s*[^.]+?(?:\.|$)/gi, '').trim();

  // 2. Extract second choice strategy
  let secondChoice = null;
  const scMatch = remaining.match(/Second Choice:\s*(.+?)(?:\.\s*$|\.$|$)/i);
  if (scMatch) {
    secondChoice = scMatch[1].trim().replace(/\.+$/, '');
    remaining = remaining.replace(/Second Choice:\s*.+?(?:\.\s*$|\.$|$)/i, '').trim();
  }

  // 3. Remove "Qty: N" artifacts
  remaining = remaining.replace(/Qty:\s*\d+\.?\s*/gi, '').trim();

  // 5. Extract recipe code (alphanumeric pattern at start, e.g., V7159d, TBC04-1, TEV72-9)
  //    Handle duplicated codes (e.g., "TBC04-1 TBC04-1-")
  let code = null;
  const codeMatch = remaining.match(/^([A-Z]{1,5}\d{2,5}[a-zA-Z]?(?:-\d+)?)\s*(?:\1)?[\s-]*/i);
  if (codeMatch) {
    code = codeMatch[1].toUpperCase();
    remaining = remaining.slice(codeMatch[0].length).replace(/^[-–—]\s*/, '').trim();
  }

  // 6. Try to split into heading (arrangement name) and body (description)
  let heading = null;
  let body = remaining;

  // Clean trailing punctuation/whitespace
  body = body.replace(/[,.\s]+$/, '').trim();

  if (!body) {
    return { ...empty, code, addOns, secondChoice };
  }

  // Short descriptions (< 80 chars) — the whole thing is the heading
  if (body.length < 80) {
    heading = body;
    body = null;
  } else {
    // Try "Name - description" pattern (capitalized phrase, then dash, then lowercase)
    const dashSep = body.match(/^([A-Z][^-–—]{2,50})[\s]*[-–—][\s]+(.+)/s);
    if (dashSep) {
      heading = dashSep[1].trim().replace(/[-–—\s]+$/, '');
      body = dashSep[2].trim().replace(/[,.\s]+$/, '');
    } else {
      // Try "Name: description" pattern
      const colonSep = body.match(/^([A-Z][^:]{2,50}):\s+(.+)/s);
      if (colonSep) {
        heading = colonSep[1].trim();
        body = colonSep[2].trim().replace(/[,.\s]+$/, '');
      } else {
        // No clear name — show everything as body, no heading
        heading = null;
      }
    }
  }

  return { code, heading, body, addOns, secondChoice };
}


function ReferenceReadStatus({ references }) {
  const messages = [];
  if (references.productState === 'loading') messages.push('Loading catalog details…');
  if (references.productState === 'unavailable') messages.push('Catalog details are unavailable.');
  if (references.floristState === 'loading') messages.push('Checking sending-florist link…');
  if (references.floristState === 'unavailable') messages.push('Sending-florist link is unavailable.');
  return messages.length ? (
    <p role="status" className="mt-2 text-xs text-stone-500" data-testid="brief-reference-status">
      {messages.join(' ')}
    </p>
  ) : null;
}

// Reference photo URL for a resolved product: the size-specific photo when one
// exists, else the product default, else null (the slot then shows a placeholder).
// Served by the public GET /products/{id}/photo; `v` busts the cache on replace.
// Reference-image slot — a permanent part of the recipe card. Shows the matched
// product's reference photo when one exists (via `imageUrl`); until then it
// renders a labeled placeholder and behaves like an order with no reference image.
function ReferenceImageSlot({ referenceImage }) {
  const [lightbox, setLightbox] = useState(false);
  const { imageUrl } = referenceImage;

  if (!imageUrl) {
    return (
      <div
        className="w-[76px] h-[76px] flex-shrink-0 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 flex flex-col items-center justify-center text-center px-1"
        data-testid="brief-reference-slot"
      >
        <ImageIcon className="h-4 w-4 text-stone-300" />
        <span
          className="text-[8px] font-medium text-stone-400 leading-tight mt-1"
          data-testid={referenceImage.configured
            ? 'brief-reference-unavailable'
            : 'brief-reference-empty'}
        >
          {productReferenceMediaLabel(referenceImage, 'Reference example')}
        </span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setLightbox(true)}
        className="w-[76px] h-[76px] flex-shrink-0 rounded-lg overflow-hidden border border-stone-200"
        data-testid="brief-reference-thumb"
      >
        <img src={imageUrl} alt="Recipe reference example" className="w-full h-full object-cover" />
      </button>
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
          data-testid="brief-reference-lightbox"
        >
          <img src={imageUrl} alt="Recipe reference example" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </>
  );
}

// The catalog recipe the order links to via owner + code (florist-first). It is
// a *suggestion*, not verified truth — the match is auto-derived from the code and
// nothing has corroborated it yet (see the corroboration/Check-1 spec in BACKLOG).
// So it's framed as an "AI-matched suggestion," sits BELOW the extracted
// description (the source of truth), and tells the designer to verify.
// Renders nothing until an order resolves to a catalog product with a recipe.
// Corroboration-gated (Check 1): when the order's own description corroborates
// the code-matched recipe, this stands as the single VERIFIED source of truth
// (green) and the extracted description collapses. When it does NOT corroborate,
// it shows amber alongside the extracted description with a "check the code" nudge
// — we never hide the extracted info on an unverified match. See services/corroboration.py.
function CatalogRecipeBlock({ resolved }) {
  if (!resolved?.resolved || !resolved.variant?.recipe_text) return null;
  const verified = resolved.corroboration?.status === 'verified';
  const meta = [resolved.product?.product_name, resolved.size_label].filter(Boolean).join(' · ');
  const t = verified
    ? { wrap: 'border-emerald-200 bg-emerald-50/50', head: 'text-emerald-700', chip: 'text-emerald-600 bg-emerald-100',
        Icon: Check, label: 'Catalog Recipe', chipText: 'Verified',
        caption: "Verified against this order's description." }
    : { wrap: 'border-amber-300 bg-amber-50/60', head: 'text-amber-800', chip: 'text-amber-700 bg-amber-100',
        Icon: AlertCircle, label: 'AI-Matched Recipe', chipText: 'Check code',
        caption: "Order description doesn't clearly match this product — verify the code against the order above." };
  return (
    <div className={`rounded-lg border ${t.wrap} px-4 py-3`} data-testid="brief-catalog-recipe" data-corroboration={verified ? 'verified' : 'discrepancy'}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${t.head} uppercase tracking-wider`}>
          <t.Icon className="h-3.5 w-3.5" />{t.label}
        </span>
        <span className={`text-[9px] font-bold ${t.chip} px-1.5 py-0.5 rounded-full uppercase tracking-wide`}>{t.chipText}</span>
      </div>
      {meta && <div className="text-[11px] text-stone-500 mt-1">{meta}</div>}
      <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed mt-2" data-testid="brief-catalog-recipe-text">
        {resolved.variant.recipe_text}
      </div>
      <p className="text-[10px] text-stone-400 italic mt-2">{t.caption}</p>
    </div>
  );
}

// Mobile single-column layout for the order-detail screen (option 3a). Rendered
// inside DesignView's full-screen overlay via <DesignerBrief variant="mobile">.
// Owns the compact identity bar (with Back) and the sticky bottom action; the
// app's global nav and the Design sub-header sit above it, untouched. Reuses the
// parent's computed recipe/source/productLink and the shared card components.
function MobileDesignerBrief({
  order, recipe, displayHeading, resolvedProduct, referenceImage, references, sourceLabel, sourceStyle, productLink, documentUrl,
  onMarkDesigned, actionPending = false, onBack, handlePrintTrifold, onPrintOrder, onNoteSaved,
}) {

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-white" data-testid="designer-brief">

      {/* Identity bar (sticky top) */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-200 flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center text-stone-600 hover:text-stone-900 flex-shrink-0"
          aria-label="Back"
          data-testid="mobile-brief-back"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="text-sm font-mono text-stone-400 flex-shrink-0" data-testid="brief-internal-order-number">
          {order?.internal_order_number || '—'}
        </span>
        <span className="text-stone-300 flex-shrink-0">·</span>
        <span className="text-sm font-semibold text-stone-900 truncate flex-1" data-testid="brief-recipient-name">
          {order?.recipient_name || 'Unknown'}
        </span>
        {order?.delivery_time_requirement && (
          <span
            className="text-xs font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full flex-shrink-0"
            data-testid="brief-target-time-chip"
          >
            {order.delivery_time_requirement}
          </span>
        )}
      </div>

      {/* Scroll area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
        {order?.desktop_sync?.edit_status && <p className="text-xs text-amber-700">{order.desktop_sync.edit_status}. Printing uses confirmed values for edits awaiting review.</p>}

        {/* Design Recipe (leads) */}
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden" data-testid="brief-recipe-card">
          <div className="flex items-center justify-between px-4 py-2 bg-sky-100/60">
            <span className="text-[11px] font-bold text-stone-600 uppercase tracking-wider">Design Recipe</span>
            <span
              className={`px-2.5 py-0.5 rounded text-[11px] font-bold tracking-wide ${sourceStyle.bg} ${sourceStyle.text}`}
              data-testid="brief-source-badge"
            >
              {sourceLabel}
            </span>
          </div>
          <div className="px-4 py-3">
            <div className="flex gap-3 items-start">
              <ReferenceImageSlot
                referenceImage={referenceImage}
              />
              <div className="flex-1 min-w-0">
                {recipe.code && (
                  <div className="text-xs font-mono text-stone-400" data-testid="brief-recipe-code">{recipe.code}</div>
                )}
                {displayHeading && (
                  <h2 className="text-2xl font-extrabold text-stone-900 leading-tight tracking-tight mt-0.5" data-testid="brief-product-name">
                    {displayHeading}
                  </h2>
                )}
              </div>
            </div>

            <ReferenceReadStatus references={references} />
            {recipe.body && resolvedProduct?.corroboration?.status !== 'verified' && (
              <p className="text-base text-stone-600 leading-relaxed mt-2" data-testid="brief-product-body">{recipe.body}</p>
            )}
            {!displayHeading && !recipe.body && !recipe.code && (
              <p className="text-base text-stone-300 italic">No product description available</p>
            )}

            {recipe.addOns.length > 0 && (
              <>
                <div className="border-t border-dashed border-stone-200 my-3" />
                <span className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <Gift className="h-3.5 w-3.5" />Add-ons
                </span>
                <ul className="space-y-1">
                  {recipe.addOns.map((addon) => (
                    <li key={addon} className="text-sm text-stone-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-stone-400 flex-shrink-0" />{addon}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {recipe.secondChoice && (
              <>
                <div className="border-t border-dashed border-stone-200 my-3" />
                <span className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider block mb-1.5">Second Choice Strategy</span>
                <p className="text-sm text-stone-500 italic">{recipe.secondChoice}</p>
              </>
            )}

            {/* AI-matched catalog recipe (suggestion) — below the extracted description */}
            {resolvedProduct?.resolved && resolvedProduct.variant?.recipe_text && (
              <div className="mt-3">
                <CatalogRecipeBlock resolved={resolvedProduct} />
              </div>
            )}

            {productLink && (
              <>
                <div className="border-t border-stone-200 my-3" />
                <a href={productLink.href} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700"
                  data-testid="brief-order-source-btn">
                  <ExternalLink className="h-4 w-4" />{productLink.label}
                </a>
              </>
            )}

            {order?.document_id && documentUrl && (
              <>
                <div className="border-t border-stone-200 my-3" />
                <AuthenticatedDocumentLink url={documentUrl}
                  documentKey={JSON.stringify([order?.id, order?.document_id, order?.resolved_source || order?.network_source, order?.desktop_sync?.origin])}
                  orderNumber={order?.internal_order_number || order?.order_number}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700"
                  data-testid="brief-view-original-btn">
                  <FileText className="h-4 w-4" />View Original Order
                </AuthenticatedDocumentLink>
              </>
            )}

            {/* Print Order Sheet — available on every order */}
            <div className="border-t border-stone-200 my-3" />
            <button
              type="button"
              onClick={onPrintOrder}
              className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700"
              data-testid="brief-print-order-btn"
            >
              <Printer className="h-4 w-4" />Print Order
            </button>
          </div>
        </div>

        {/* Logistics */}
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden" data-testid="brief-logistics-card">
          <div className="bg-sky-100/60 px-4 py-2">
            <span className="text-[11px] font-bold text-stone-600 uppercase tracking-wider">Logistics</span>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-stone-500">Target Time</span>
              <span className={`text-sm font-bold ${order?.delivery_time_requirement ? 'text-rose-600' : 'text-stone-300'}`} data-testid="brief-target-time">
                {order?.delivery_time_requirement || '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-500">Order Value</span>
              <span className="text-xl font-bold text-stone-900" data-testid="brief-order-value">
                {order?.total_price != null ? `$${order.total_price.toFixed(2)}` : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Special Instructions — moved under Logistics */}
        {order?.special_instructions && (
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg" style={{ backgroundColor: '#FFF9DB' }} data-testid="brief-special-instructions">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-stone-700">
              <span className="font-bold uppercase tracking-wide">Special Instructions: </span>{order.special_instructions}
            </p>
          </div>
        )}

        {/* Recipient */}
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden" data-testid="brief-recipient-card">
          <div className="bg-sky-100/60 px-4 py-2">
            <span className="text-[11px] font-bold text-stone-600 uppercase tracking-wider">Recipient</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-sm font-bold text-stone-900 mb-1">{order?.recipient_name || '—'}</p>
            <p className="text-sm text-stone-500 leading-relaxed">
              {order?.recipient_street_address || ''}
              {order?.recipient_street_address_2 && <><br />{order.recipient_street_address_2}</>}
              {(order?.recipient_city || order?.recipient_state || order?.recipient_zip_code) && (
                <><br />{[order.recipient_city, order.recipient_state, order.recipient_zip_code].filter(Boolean).join(', ')}</>
              )}
            </p>
          </div>
        </div>

        {/* Message */}
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden" data-testid="brief-enclosure-card">
          <div className="flex items-center justify-between px-4 py-2 bg-sky-100/60">
            <span className="text-[11px] font-bold text-stone-600 uppercase tracking-wider">Message</span>
            <button onClick={handlePrintTrifold} className="flex items-center gap-1 text-xs font-semibold text-purple-600 uppercase tracking-wider" data-testid="brief-print-btn">
              <Printer className="h-4 w-4" />Print
            </button>
          </div>
          <div className="px-4 py-3">
            <p className="text-base text-stone-700 italic leading-relaxed whitespace-pre-line" data-testid="brief-enclosure-message">
              {order?.card_message ? `"${order.card_message}"` : '—'}
            </p>
          </div>
        </div>

        {/* Your Photo (designer's own capture) */}
        <ArrangementPhotoCard
          key={order?.id}
          orderId={order?.id}
          initialPhoto={order?.arrangement_photo || null}
          assignedDesignerId={order?.assigned_designer_id || null}
        />

        {/* Designer Notes */}
        <DesignerNotesCard key={`designer-notes-${order?.id}`} order={order} onNoteSaved={onNoteSaved} />
      </div>

      {/* Sticky bottom action */}
      {order?.status === 'accepted' && (
        <div
          className="flex-shrink-0 px-3 pt-2 border-t border-stone-200 bg-white"
          style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        >
          <Button
            disabled={actionPending}
            onClick={() => onMarkDesigned?.(order.id)}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full text-base gap-2 shadow-md"
            data-testid="brief-mark-designed-btn"
          >
            Mark as Designed <Check className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}


const DesignerBrief = ({ order, onMarkDesigned, actionPending = false, documentUrl, variant = 'desktop', onBack, onNoteSaved }) => {
  const [confirmTrifold, setConfirmTrifold] = useState(null);
  const printSession = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  // Retire the confirmation, including A -> B -> A selection/session changes.
  useLayoutEffect(() => { setConfirmTrifold(null); }, [order?.id, printSession, variant]);

  // --- LIVE DATA ---
  const sourceLabel = orderSourceLabel(order, 'Manual');
  const sourceStyle = SOURCE_STYLES[sourceLabel] || DEFAULT_SOURCE_STYLE;

  // Shop settings (for trifold print footer)
  const [shopState, setShopState] = useState(null);
  const shop = shopState?.session === printSession ? shopState.value : null;
  useEffect(() => {
    let cancelled = false;
    getPublicShopBranding()
      .then(data => {
        if (!cancelled && printSession === getActiveSessionRevision()) {
          setShopState({ value: data, session: printSession });
        }
      })
      .catch(() => {
        if (!cancelled && printSession === getActiveSessionRevision()) setShopState(null);
      });
    return () => { cancelled = true; };
  }, [printSession]);

  const startTrifoldPrint = usePreparedPrint({
    kind: 'trifold', record: order, shop, fetchBranding: false,
  });
  const handlePrintTrifold = () => startTrifoldPrint();
  const openTrifoldConfirmation = () => {
    if (!order?.id || printSession !== getActiveSessionRevision()) return;
    setConfirmTrifold({ order: captureTrifoldOrder(order), session: printSession });
  };
  const confirmTrifoldPrint = () => {
    if (confirmTrifold?.order?.id === order?.id
        && confirmTrifold.session === printSession
        && confirmTrifold.session === getActiveSessionRevision()) {
      startTrifoldPrint(confirmTrifold.order);
    }
    setConfirmTrifold(null);
  };

  const handlePrintOrderSheet = useOrderSheetPrint({ order, shop, fetchBranding: false });

  const references = useDesignerBriefReferences(order?.id);
  const resolvedSubsource = references.florist;
  const resolvedProduct = references.product;

  // Product link: resolved via subsource → sending florist mapping
  const productLink = useMemo(() => {
    if (!resolvedSubsource?.resolved) return null;
    const url = resolvedSubsource.sending_florist?.product_url;
    if (!url) return null;
    const href = url.startsWith('http') ? url : `https://${url}`;
    return {
      href,
      label: `View Product on ${resolvedSubsource.sending_florist.name}`,
    };
  }, [resolvedSubsource]);

  const recipe = useMemo(
    () => {
      const parsed = parseProductDescription(order?.product_description);
      // Use explicit AI-extracted fields as overrides if available
      if (order?.product_code && !parsed.code) parsed.code = order.product_code;
      if (order?.product_name && !parsed.heading) parsed.heading = order.product_name;
      return parsed;
    },
    [order?.product_description, order?.product_code, order?.product_name]
  );

  // Heading shown on the recipe card: prefer the resolved catalog product's real
  // name (a clean title) when the order links to one; otherwise fall back to the
  // parsed/extracted heading. Either way, cap it so it stays ~one row.
  const displayHeading = capHeading(
    (resolvedProduct?.resolved && resolvedProduct.product?.product_name) || recipe.heading
  );

  // Reference photo of the matched product (size-specific → default → none).
  const referenceImage = useProductReferenceMedia(resolvedProduct?.resolved ? resolvedProduct : null, references.mediaOwner);

  if (variant === 'mobile') {
    return (
      <MobileDesignerBrief
        order={order}
        recipe={recipe}
        displayHeading={displayHeading}
        resolvedProduct={resolvedProduct}
        referenceImage={referenceImage}
        references={references}
        sourceLabel={sourceLabel}
        sourceStyle={sourceStyle}
        productLink={productLink}
        documentUrl={documentUrl}
        onMarkDesigned={onMarkDesigned}
        actionPending={actionPending}
        onBack={onBack}
        onNoteSaved={onNoteSaved}
        handlePrintTrifold={handlePrintTrifold}
        onPrintOrder={handlePrintOrderSheet}
      />
    );
  }

  const trustState = resolveTrustState(order, resolvedProduct);
  const hasCatalogRecipe = !!(resolvedProduct?.resolved && resolvedProduct.variant?.recipe_text);

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white" data-testid="designer-brief">

      {/* ── Top bar: identity + actions ── */}
      <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-stone-200 flex-shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className={`px-2 py-0.5 rounded text-[11px] font-bold tracking-wide ${sourceStyle.bg} ${sourceStyle.text}`}
              data-testid="brief-source-badge"
            >
              {sourceLabel}
            </span>
            <span className="text-xs text-stone-400 font-mono" data-testid="brief-internal-order-number">
              {order?.internal_order_number || '—'}
            </span>
            {order?.order_number && (
              <span className="text-xs text-stone-300 font-mono" data-testid="brief-order-number">#{order.order_number}</span>
            )}
          </div>
          <h1 className="text-2xl font-extrabold text-stone-900 tracking-tight truncate" data-testid="brief-recipient-name">
            {order?.recipient_name || 'Unknown Recipient'}
          </h1>
          {order?.desktop_sync?.edit_status && <p className="text-xs text-amber-700">{order.desktop_sync.edit_status}. Printing uses confirmed values for edits awaiting review.</p>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={openTrifoldConfirmation}
            className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 h-11 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors"
            data-testid="brief-print-trifold-btn"
          >
            <Printer className="h-4 w-4" />
            Print Trifold Card
          </button>
          <div className="h-6 w-px bg-stone-300" aria-hidden="true" />
          {order?.status === 'accepted' ? (
            <Button
              disabled={actionPending}
              onClick={() => onMarkDesigned?.(order.id)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full px-5 h-11 text-base gap-2 shadow-md"
              data-testid="brief-mark-designed-btn"
            >
              Mark as Designed <Check className="h-4 w-4" />
            </Button>
          ) : (
            order?.status && (
              <span className="text-sm text-stone-400 px-2 capitalize">{order.status.replace(/_/g, ' ')}</span>
            )
          )}
        </div>
      </div>

      {/* ── Split body: source preview (left ~60%) + value-add rail (right ~40%) ── */}
      <div className="flex-1 min-h-0 flex">

        {/* Left: the original document, or the generated order sheet */}
        <div className="flex-[55] min-w-0 border-r border-stone-200" data-testid="brief-preview-pane">
          <OrderSourcePreview order={order} documentUrl={documentUrl} shop={shop} />
        </div>

        {/* Right: value-add rail */}
        <div className="flex-[45] min-w-0 overflow-y-auto p-4 space-y-4" data-testid="brief-rail">

          {/* Quick-glance chip strip — trust, reliable extractions, and quick actions.
              flex-wrap so a narrow rail wraps to another line instead of clipping. */}
          <div className="flex flex-wrap items-center gap-2" data-testid="brief-chips">
            <TrustBadge state={trustState} />
            {order?.size && (
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold tracking-wide ${SIZE_STYLES[order.size.toLowerCase()] || 'bg-stone-200 text-stone-700'}`}
                data-testid="chip-size"
              >
                {order.size.toUpperCase()}
              </span>
            )}
            {order?.total_price != null && (
              <span className="inline-flex items-center rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-bold text-stone-700" data-testid="chip-value">
                ${order.total_price.toFixed(2)}
              </span>
            )}
            {order?.delivery_time_requirement && (
              <span className="inline-flex items-center rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-bold text-stone-700" data-testid="chip-time">
                {order.delivery_time_requirement}
              </span>
            )}
            {productLink && (
              <a
                href={productLink.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700 hover:bg-sky-100 transition-colors"
                data-testid="brief-recipe-url"
                title={productLink.label}
              >
                Recipe URL <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <button
              type="button"
              onClick={handlePrintOrderSheet}
              className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-bold text-stone-600 hover:bg-stone-100 transition-colors"
              data-testid="brief-print-order-btn"
              title="Print the generated order sheet"
            >
              Print Sheet <Printer className="h-3 w-3" />
            </button>
            <NoteChip order={order} onNoteSaved={onNoteSaved} />
          </div>

          {/* Products to Design — the product photo and the recipe/description
              together in one box. (Multi-item carousel to flip through several
              products comes later.) */}
          <div className="rounded-lg border border-stone-200 overflow-hidden" data-testid="brief-products-box">
            {/* Labeled carousel nav (Claude Design) — big, high-contrast controls
                for 40–60+ designers: outlined Previous/Next buttons + a filled
                count pill. Inert today (one product per order); wired to flip the
                photo + recipe together when multi-item orders land. */}
            <div className="flex items-center justify-between gap-4 px-4 py-3 bg-sky-100">
              <span className="text-lg font-extrabold text-slate-800 uppercase tracking-wide">Products to Design</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
                  aria-label="Previous product"
                  data-testid="products-prev"
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
                  Previous
                </button>
                <span
                  className="inline-flex items-center rounded-lg bg-slate-800 px-4 py-1.5 text-sm font-bold text-white tabular-nums"
                  data-testid="products-counter"
                >
                  Product 1 of 1
                </span>
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
                  aria-label="Next product"
                  data-testid="products-next"
                >
                  Next
                  <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
            <div className="flex gap-4 items-start px-4 py-3" data-testid="brief-photo-recipe-row">
              <div className="flex-1 min-w-0">
                <MatchedPhotoTile
                  key={order?.id}
                  label="Product Photo"
                  imageUrl={referenceImage.imageUrl}
                  referenceImage={referenceImage}
                  trustState={trustState}
                />
              </div>

              {/* Recipe/Description — matched catalog recipe, else the AI extraction */}
              <div className="flex-1 min-w-0" data-testid="brief-recipe-card">
                <span className="block text-sm font-bold text-stone-600 uppercase tracking-wider mb-1">Recipe/Description</span>
                {recipe.code && (
                  <div className="text-xs font-mono font-semibold text-stone-400 tracking-wide" data-testid="brief-recipe-code">
                    {recipe.code}
                  </div>
                )}
                {displayHeading && (
                  <h2 className="font-display text-2xl font-bold text-stone-900 leading-snug mt-0.5" data-testid="brief-product-name">
                    {displayHeading}
                  </h2>
                )}

                <ReferenceReadStatus references={references} />
                {hasCatalogRecipe ? (
                  <div className="mt-2 text-sm text-stone-800 whitespace-pre-wrap leading-relaxed" data-testid="brief-catalog-recipe-text">
                    {resolvedProduct.variant.recipe_text}
                  </div>
                ) : (
                  <>
                    {recipe.body && (
                      <p className="text-sm text-stone-600 leading-relaxed mt-2" data-testid="brief-product-body">{recipe.body}</p>
                    )}
                    {!displayHeading && !recipe.body && !recipe.code && (
                      <p className="text-sm text-stone-300 italic">No product description available</p>
                    )}
                  </>
                )}

                {recipe.addOns.length > 0 && (
                  <>
                    <div className="border-t border-dashed border-stone-200 my-3" />
                    <span className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                      <Gift className="h-3.5 w-3.5" />Add-ons
                    </span>
                    <ul className="space-y-1">
                      {recipe.addOns.map((addon) => (
                        <li key={addon} className="text-sm text-stone-700 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-stone-400 flex-shrink-0" />{addon}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {recipe.secondChoice && (
                  <>
                    <div className="border-t border-dashed border-stone-200 my-3" />
                    <span className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider block mb-1.5">Second Choice Strategy</span>
                    <p className="text-sm text-stone-500 italic">{recipe.secondChoice}</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Arrangement photo — moved below the reference/recipe row; fills once
              captured on mobile. The rest of this row is reserved for future
              per-order-type content (e.g. occasion / colors on ecommerce orders). */}
          <ArrangementPhotoTile key={order?.id} order={order} assignedDesignerId={order?.assigned_designer_id || null} />
        </div>
      </div>

      {/* ── Trifold print confirm: the card prints the extracted message; check it first ── */}
      {confirmTrifold && confirmTrifold.order.id === order?.id && confirmTrifold.session === printSession && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="trifold-confirm"
          onClick={() => setConfirmTrifold(null)}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
              <span className="text-sm font-bold text-stone-800">Print enclosure card</span>
              <button type="button" onClick={() => setConfirmTrifold(null)} className="text-stone-400 hover:text-stone-700" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-xs text-stone-500 mb-2">This message will print on the card. Check it against the order on the left before printing.</p>
              <div className="rounded-lg bg-stone-50 border border-stone-200 px-4 py-3">
                <p className="text-base text-stone-800 italic whitespace-pre-line" data-testid="trifold-confirm-message">
                  {confirmTrifold.order.card_message ? `"${confirmTrifold.order.card_message}"` : 'No card message on this order.'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-stone-200">
              <button type="button" onClick={() => setConfirmTrifold(null)} className="text-sm font-semibold text-stone-500 hover:text-stone-800 px-3 py-2">Cancel</button>
              <Button
                onClick={confirmTrifoldPrint}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-full px-5 h-10 gap-2"
                data-testid="trifold-confirm-print"
              >
                <Printer className="h-4 w-4" /> Print Card
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default DesignerBrief;
